import React, { useState, useEffect } from "react";
import {
  Star,
  X,
  Send,
  AlertCircle,
  User,
  EyeOff,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-toastify";
import communityService from "../../services/communityService";
import "./CommunityRatingModal.css";

const CommunityRatingModal = ({
  isOpen,
  onClose,
  communityId,
  communityName,
  onRatingSubmitted,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [hasExistingRating, setHasExistingRating] = useState(false);

  const [ratings, setRatings] = useState({
    overall: 5,
    discussionQuality: 5,
    helpfulness: 5,
    knowledgeSharing: 5,
    communitySupport: 5,
  });

  const [feedback, setFeedback] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);

  // Rating categories with descriptions
  const ratingCategories = [
    {
      key: "overall",
      label: "Overall Experience",
      description: "Your general satisfaction with this community",
    },
    {
      key: "discussionQuality",
      label: "Discussion Quality",
      description: "How valuable and engaging are the discussions?",
    },
    {
      key: "helpfulness",
      label: "Helpfulness",
      description: "How helpful are other members when you have questions?",
    },
    {
      key: "knowledgeSharing",
      label: "Knowledge Sharing",
      description: "Quality of research and knowledge shared in the community",
    },
    {
      key: "communitySupport",
      label: "Community Support",
      description: "How supportive and welcoming is the community?",
    },
  ];

  // Load existing rating when modal opens
  useEffect(() => {
    if (isOpen && communityId) {
      loadExistingRating();
    }
  }, [isOpen, communityId]);

  const loadExistingRating = async () => {
    try {
      setLoadingExisting(true);
      const response = await communityService.getMyCommunityRating(communityId);
if (response.success && response.data) {
        // The backend returns data in this structure: response.data.rating.ratings
        const ratingObject = response.data.rating;
        const ratingsData = ratingObject?.ratings;
        const feedbackData = ratingObject?.feedback;
        const anonymousData = ratingObject?.isAnonymous;
if (ratingsData && (ratingsData.overall || ratingsData.overall === 0)) {
          setHasExistingRating(true);
          // Load the actual ratings from the backend
          const existingRatings = {
            overall: ratingsData.overall || 5,
            discussionQuality: ratingsData.discussionQuality || 5,
            helpfulness: ratingsData.helpfulness || 5,
            knowledgeSharing: ratingsData.knowledgeSharing || 5,
            communitySupport: ratingsData.communitySupport || 5,
          };
setRatings(existingRatings);
          setFeedback(feedbackData || "");
          setIsAnonymous(anonymousData || false);
        } else {
setHasExistingRating(false);
        }
      } else {
setHasExistingRating(false);
      }
    } catch (error) {
// No existing rating found (expected for new ratings)
      setHasExistingRating(false);
    } finally {
      setLoadingExisting(false);
    }
  };

  const handleRatingChange = (category, value) => {
setRatings((prev) => {
      const newRatings = {
        overall: 5,
        discussionQuality: 5,
        helpfulness: 5,
        knowledgeSharing: 5,
        communitySupport: 5,
        ...prev,
        [category]: value,
      };
return newRatings;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      const ratingData = {
        ratings,
        feedback: feedback.trim(),
        isAnonymous,
      };

      const response = await communityService.submitCommunityRating(
        communityId,
        ratingData
      );

      if (response.success) {
        toast.success(
          hasExistingRating
            ? "Rating updated successfully!"
            : "Rating submitted successfully!"
        );
        onRatingSubmitted?.(response.data);
        onClose();
      }
    } catch (error) {
toast.error(error.message || "Failed to submit rating");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRatings({
      overall: 5,
      discussionQuality: 5,
      helpfulness: 5,
      knowledgeSharing: 5,
      communitySupport: 5,
    });
    setFeedback("");
    setIsAnonymous(false);
    setHasExistingRating(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getRatingText = (value) => {
    const texts = {
      1: "Poor",
      2: "Fair",
      3: "Good",
      4: "Very Good",
      5: "Excellent",
    };
    return texts[value] || "";
  };

  if (!isOpen) return null;

  return (
    <div className="rating-modal-overlay" onClick={handleClose}>
      <div className="rating-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rating-modal-header">
          <div className="modal-title">
            <Star size={24} />
            <div>
              <h2>
                {hasExistingRating ? "Update Your Rating" : "Rate Community"}
              </h2>
              <p>Share your experience with "{communityName}"</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="close-button"
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        {loadingExisting ? (
          <div className="rating-modal-loading">
            <RefreshCw size={24} className="spinning" />
            <p>Loading your existing rating...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="rating-modal-content">
            {hasExistingRating && (
              <div className="existing-rating-notice">
                <AlertCircle size={16} />
                <span>
                  You have already rated this community. You can update your
                  rating below.
                </span>
              </div>
            )}

            {/* Rating Categories */}
            <div className="rating-categories">
              {ratingCategories.map((category) => {
                const currentRating = ratings?.[category.key] || 5;
return (
                  <div key={category.key} className="rating-category">
                    <div className="category-info">
                      <h4>{category.label}</h4>
                      <p>{category.description}</p>
                    </div>

                    <div className="rating-input">
                      <div className="stars-input">
                        {[1, 2, 3, 4, 5].map((star) => {
                          const isFilled = star <= currentRating;
                          return (
                            <button
                              key={star}
                              type="button"
                              onClick={() =>
                                handleRatingChange(category.key, star)
                              }
                              className={`star-button ${
                                isFilled ? "filled" : "empty"
                              }`}
                            >
                              <Star size={20} />
                            </button>
                          );
                        })}
                      </div>
                      <span className="rating-text">
                        {getRatingText(currentRating)} ({currentRating}/5)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Feedback */}
            <div className="feedback-section">
              <label htmlFor="feedback">Additional Feedback (Optional)</label>
              <textarea
                id="feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Share more details about your experience..."
                maxLength={500}
                rows={4}
              />
              <div className="character-count">
                {feedback.length}/500 characters
              </div>
            </div>

            {/* Anonymous Option */}
            <div className="anonymous-section">
              <label className="anonymous-checkbox">
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                />
                <div className="checkbox-icon">
                  {isAnonymous ? <EyeOff size={16} /> : <User size={16} />}
                </div>
                <div className="checkbox-content">
                  <span>Submit anonymously</span>
                  <small>Your name will not be shown with this rating</small>
                </div>
              </label>
            </div>

            {/* Submit Button */}
            <div className="rating-modal-actions">
              <button
                type="button"
                onClick={handleClose}
                className="btn secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button type="submit" className="btn primary" disabled={loading}>
                {loading ? (
                  <>
                    <RefreshCw size={16} className="spinning" />
                    {hasExistingRating ? "Updating..." : "Submitting..."}
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    {hasExistingRating ? "Update Rating" : "Submit Rating"}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CommunityRatingModal;
